/**
 * Public Privacy Policy page — accessible without login.
 * URL: /privacy
 * Used to satisfy Meta App Review requirements.
 */
export default function Privacy() {
  const today = new Date().toLocaleDateString("en-MY", { year: "numeric", month: "long", day: "numeric" });

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100">
      <div className="max-w-3xl mx-auto px-6 py-16">
        {/* Header */}
        <div className="mb-12 pb-8 border-b border-zinc-800">
          <div className="text-[10px] font-mono tracking-[0.2em] uppercase text-zinc-500 mb-2">// Underads Metaverse</div>
          <h1 className="font-display text-4xl sm:text-5xl font-semibold mb-3">Privacy Policy</h1>
          <p className="text-sm text-zinc-500 font-mono">Last updated: {today}</p>
        </div>

        <div className="prose prose-invert max-w-none space-y-8 text-sm leading-relaxed text-zinc-300">
          {/* Intro */}
          <section>
            <p>
              <strong>Underads Metaverse</strong> ("we", "our", "the platform") is an internal marketing tool operated by{" "}
              <strong>Underads Capital Sdn Bhd</strong> ("Underads", "the Company") to help our internal marketing team manage Meta (Facebook & Instagram)
              advertising campaigns.
            </p>
            <p className="mt-3">
              This Privacy Policy explains what data we collect, how we use it, and how we protect it. By using this platform, you agree to the practices
              described below.
            </p>
          </section>

          {/* 1. Who can use */}
          <section>
            <h2 className="font-display text-2xl font-semibold mb-3 text-zinc-100">1. Who Can Use This Platform</h2>
            <p>
              This platform is restricted to authorised employees, contractors, and authorised representatives of Underads Capital Sdn Bhd. It is{" "}
              <strong>not available to the general public</strong>. Access requires a username and password issued by an administrator.
            </p>
          </section>

          {/* 2. Data we collect */}
          <section>
            <h2 className="font-display text-2xl font-semibold mb-3 text-zinc-100">2. Data We Collect</h2>
            <p>To operate the platform, we collect and process the following data:</p>
            <ul className="list-disc pl-6 mt-3 space-y-2">
              <li>
                <strong>Account credentials:</strong> Username, hashed password, role (admin or member), and display name. Passwords are hashed using
                industry-standard bcrypt and are never stored in plain text.
              </li>
              <li>
                <strong>Meta advertising data (read):</strong> Through the Meta Marketing API, we read advertising data from ad accounts owned by Underads
                Capital and assigned to the connected Business Manager. This includes campaigns, ad sets, ads, creative content, performance metrics
                (spend, impressions, clicks, CTR, conversions), and audience breakdowns (age range, gender, country, placement). We do not access data from
                ad accounts we do not own.
              </li>
              <li>
                <strong>Meta advertising data (write):</strong> When authorised users explicitly request it (with confirmation), we may pause/resume
                campaigns, update budgets, and create new campaigns/ad sets/ads on Meta on behalf of Underads Capital.
              </li>
              <li>
                <strong>Facebook Page posts:</strong> When the "boost existing post" feature is used, we read the list of recent posts from the Facebook
                Page configured by an administrator (only Pages owned by Underads Capital).
              </li>
              <li>
                <strong>Audit logs:</strong> Every write action (pause, resume, budget update, ad creation) is logged with the user identity, timestamp,
                target object, and outcome.
              </li>
              <li>
                <strong>Conversation history:</strong> AI chat conversations are stored to provide context across sessions.
              </li>
              <li>
                <strong>Application logs:</strong> Standard server logs (access timestamps, error traces) for debugging and security monitoring.
              </li>
            </ul>
          </section>

          {/* 3. How we use */}
          <section>
            <h2 className="font-display text-2xl font-semibold mb-3 text-zinc-100">3. How We Use the Data</h2>
            <ul className="list-disc pl-6 space-y-2">
              <li>To display the marketing team&apos;s Meta advertising performance in dashboards and reports.</li>
              <li>To allow authorised team members to manage campaigns more efficiently than via the native Ads Manager interface.</li>
              <li>To provide AI-powered conversational assistance (using Anthropic Claude) based on the team&apos;s real advertising context.</li>
              <li>To maintain an audit trail for accountability across the team.</li>
              <li>To diagnose and resolve technical issues with the platform.</li>
            </ul>
            <p className="mt-3">
              We <strong>do not</strong> sell, rent, or share collected data with third parties for advertising or marketing purposes outside of Underads
              Capital&apos;s own ad operations.
            </p>
          </section>

          {/* 4. Third-party */}
          <section>
            <h2 className="font-display text-2xl font-semibold mb-3 text-zinc-100">4. Third-Party Services</h2>
            <p>The platform integrates with the following third-party services strictly for the purposes stated:</p>
            <ul className="list-disc pl-6 mt-3 space-y-2">
              <li>
                <strong>Meta Platforms, Inc. (Marketing API & Graph API):</strong> To read and manage Underads Capital&apos;s own advertising data. Subject
                to Meta&apos;s{" "}
                <a href="https://developers.facebook.com/terms/" className="text-orange-400 hover:underline" target="_blank" rel="noopener noreferrer">
                  Platform Terms
                </a>
                .
              </li>
              <li>
                <strong>Anthropic, PBC (Claude AI):</strong> To process natural-language queries from authorised users. Conversations may include summarised
                advertising data for context. Subject to Anthropic&apos;s{" "}
                <a href="https://www.anthropic.com/legal/privacy" className="text-orange-400 hover:underline" target="_blank" rel="noopener noreferrer">
                  Privacy Policy
                </a>
                .
              </li>
              <li>
                <strong>Emergent (hosting platform):</strong> Application hosting infrastructure.
              </li>
            </ul>
          </section>

          {/* 5. Data Retention */}
          <section>
            <h2 className="font-display text-2xl font-semibold mb-3 text-zinc-100">5. Data Retention</h2>
            <ul className="list-disc pl-6 space-y-2">
              <li>Account credentials are kept while the user remains an authorised team member. Deactivated accounts are removed within 30 days.</li>
              <li>Meta advertising data is cached up to 30 days and refreshed on demand. Older cached data is replaced on subsequent syncs.</li>
              <li>Audit logs are retained for at least 12 months for accountability and compliance purposes.</li>
              <li>Chat conversation history is retained while the conversation is active and deleted on user request.</li>
            </ul>
          </section>

          {/* 6. Security */}
          <section>
            <h2 className="font-display text-2xl font-semibold mb-3 text-zinc-100">6. Security</h2>
            <ul className="list-disc pl-6 space-y-2">
              <li>All traffic is encrypted in transit via HTTPS/TLS.</li>
              <li>Meta API credentials (App ID, App Secret, Access Token) are stored only on the backend server in environment variables and are never exposed to the frontend.</li>
              <li>User passwords are hashed with bcrypt.</li>
              <li>Authentication uses signed JWT tokens with limited lifetime.</li>
              <li>Write actions to Meta require explicit per-action confirmation; destructive UI actions require a double-click confirm pattern.</li>
              <li>Per-user "live mode" toggle prevents accidental real-API actions by team members still in training.</li>
            </ul>
          </section>

          {/* 7. Data Deletion */}
          <section>
            <h2 className="font-display text-2xl font-semibold mb-3 text-zinc-100">7. User Rights & Data Deletion</h2>
            <p>
              Authorised users may request access to, correction of, or deletion of their personal data (account record, chat history, audit log entries
              attributed to them) by contacting the administrator listed below. Deletion requests will be honoured within 30 days, subject to legal
              retention requirements (e.g., audit log retention for accountability).
            </p>
            <p className="mt-3">
              For Meta-related data deletion: as we do not store data from any ad account we do not own, all Meta-sourced data on this platform is
              automatically purged when the corresponding ad account is removed from our Business Manager.
            </p>
          </section>

          {/* 8. Compliance */}
          <section>
            <h2 className="font-display text-2xl font-semibold mb-3 text-zinc-100">8. Compliance with Meta Platform Policies</h2>
            <p>
              This platform complies with the{" "}
              <a href="https://developers.facebook.com/devpolicy/" className="text-orange-400 hover:underline" target="_blank" rel="noopener noreferrer">
                Meta Platform Terms and Developer Policies
              </a>
              . We use Meta data solely for the purposes disclosed in this policy and only for ad accounts owned by Underads Capital Sdn Bhd within our
              own Business Manager. We do not provide a service to third-party businesses.
            </p>
          </section>

          {/* 9. Changes */}
          <section>
            <h2 className="font-display text-2xl font-semibold mb-3 text-zinc-100">9. Changes to This Policy</h2>
            <p>
              We may update this Privacy Policy from time to time. The "Last updated" date at the top of this page reflects the most recent revision.
              Material changes will be communicated to authorised users via the platform.
            </p>
          </section>

          {/* 10. Contact */}
          <section>
            <h2 className="font-display text-2xl font-semibold mb-3 text-zinc-100">10. Contact</h2>
            <p>For questions regarding this Privacy Policy or data practices, please contact:</p>
            <div className="mt-3 bg-zinc-900/50 border border-zinc-800 p-4 font-mono text-xs">
              <div><strong>Underads Capital Sdn Bhd</strong></div>
              <div>Marketing Operations</div>
              <div>Email: marketing@underadscapital.com</div>
              <div>Website: <a href="https://underadscapital.com" className="text-orange-400 hover:underline" target="_blank" rel="noopener noreferrer">underadscapital.com</a></div>
            </div>
          </section>
        </div>

        {/* Footer */}
        <div className="mt-16 pt-8 border-t border-zinc-800 text-[11px] font-mono text-zinc-600 flex items-center justify-between">
          <span>© {new Date().getFullYear()} Underads Capital Sdn Bhd. All rights reserved.</span>
          <a href="/" className="text-zinc-500 hover:text-orange-400 transition-colors">← Back to platform</a>
        </div>
      </div>
    </div>
  );
}
